import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/data/products";

export default function CartPage() {
  const { items, subtotal, removeItem, setQuantity } = useCart();
  const navigate = useNavigate();

  const shipping = subtotal > 0 && subtotal < 4000 ? 150 : 0;
  const total = subtotal + shipping;

  return (
    <div data-testid="cart-page" className="bg-[#FAF8F5]">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-16 pb-8">
        <p className="overline">Your bag</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight text-[#2A2626]">
          Bag {items.length > 0 && <span className="text-[#5C564F]">({items.length})</span>}
        </h1>
      </section>

      {items.length === 0 ? (
        <section className="max-w-[600px] mx-auto px-6 py-24 text-center">
          <p className="font-serif text-3xl text-[#2A2626]">Your bag is quiet.</p>
          <p className="mt-4 text-[#5C564F]">Discover pieces handcrafted with semi-precious crystals.</p>
          <Link
            to="/shop"
            data-testid="cart-page-shop-link"
            className="mt-10 inline-block bg-[#2A2626] text-[#FAF8F5] px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
          >
            Browse Earrings
          </Link>
        </section>
      ) : (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pb-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Line items */}
            <ul className="lg:col-span-8 divide-y divide-[#DCD5CA] border-t border-b border-[#DCD5CA]">
              {items.map((item) => (
                <li
                  key={item.id}
                  data-testid={`cart-page-item-${item.slug}`}
                  className="py-8 flex gap-6"
                >
                  <Link
                    to={`/product/${item.slug}`}
                    className="w-28 md:w-36 aspect-[4/5] bg-[#F0EBE1] overflow-hidden shrink-0"
                  >
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 flex flex-col md:flex-row md:justify-between gap-4">
                    <div className="flex-1">
                      <Link
                        to={`/product/${item.slug}`}
                        className="font-serif text-2xl text-[#2A2626] leading-tight"
                      >
                        {item.name}
                      </Link>
                      <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-[#5C564F]">
                        Handcrafted earrings
                      </p>
                      <div className="mt-6 flex items-center border border-[#DCD5CA] w-fit">
                        <button
                          onClick={() => setQuantity(item.id, item.quantity - 1)}
                          className="p-2.5 hover:bg-[#F0EBE1]"
                          aria-label="Decrease"
                        >
                          <Minus size={13} strokeWidth={1.5} />
                        </button>
                        <span className="px-5 text-sm">{item.quantity}</span>
                        <button
                          onClick={() => setQuantity(item.id, item.quantity + 1)}
                          className="p-2.5 hover:bg-[#F0EBE1]"
                          aria-label="Increase"
                        >
                          <Plus size={13} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    <div className="flex md:flex-col items-start md:items-end justify-between md:justify-start gap-2">
                      <p className="font-sans text-lg text-[#2A2626]">
                        {formatINR(item.price * item.quantity)}
                      </p>
                      <button
                        onClick={() => removeItem(item.id)}
                        data-testid={`cart-page-remove-${item.slug}`}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-[#5C564F] hover:text-[#2A2626]"
                      >
                        <X size={12} strokeWidth={1.5} /> Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <aside className="lg:col-span-4">
              <div className="bg-[#F0EBE1]/70 p-8 lg:sticky lg:top-28">
                <p className="overline">Order Summary</p>
                <dl className="mt-6 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-[#5C564F]">Subtotal</dt>
                    <dd data-testid="summary-subtotal" className="text-[#2A2626]">
                      {formatINR(subtotal)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-[#5C564F]">Shipping</dt>
                    <dd className="text-[#2A2626]">
                      {shipping === 0 ? "Complimentary" : formatINR(shipping)}
                    </dd>
                  </div>
                </dl>
                <div className="border-t border-[#DCD5CA] my-6 pt-6 flex items-baseline justify-between">
                  <span className="overline">Total</span>
                  <span data-testid="summary-total" className="font-serif text-3xl text-[#2A2626]">
                    {formatINR(total)}
                  </span>
                </div>
                <button
                  data-testid="checkout-button"
                  onClick={() => navigate("/checkout")}
                  className="w-full bg-[#2A2626] text-[#FAF8F5] py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
                >
                  Checkout · Pay Online
                </button>
                <p className="mt-4 text-[11px] text-[#5C564F] text-center">
                  Complimentary shipping over ₹4,000
                </p>
                <Link
                  to="/shop"
                  className="mt-6 block text-center text-[11px] uppercase tracking-[0.22em] text-[#2A2626] link-underline"
                >
                  Continue shopping
                </Link>
              </div>
            </aside>
          </div>
        </section>
      )}
    </div>
  );
}
